import { useEffect, useState } from "react";
import { Plus, Trash2, ArrowRight, Globe, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Button } from "../ui/button";
import AddRouteDialog from "./AddRouteDialog";

export default function GatewayRoutesList({ disabled }) {
  const [items, setItems] = useState([]);
  const [engines, setEngines] = useState([]);
  const [webTargets, setWebTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const refresh = async () => {
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/gateway/routes"),
        api.get("/gateway/engines"),
        api.get("/gateway/web-targets"),
      ]);
      setItems(r1.data);
      setEngines(r2.data);
      setWebTargets(r3.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (disabled) { setLoading(false); return; }
    refresh();
  }, [disabled]);

  const remove = async (alias) => {
    if (!window.confirm(`¿Eliminar la ruta '${alias}'?`)) return;
    try {
      await api.delete(`/gateway/routes/${alias}`);
      toast.success("Ruta eliminada");
      refresh();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const describeTarget = (r) => {
    if (r.target_type === "web_target") {
      const wt = webTargets.find((t) => t.id === r.web_target_id);
      return { icon: Globe, name: wt?.name || r.web_target_id, id: r.web_target_id, sub: "Web Target" };
    }
    const eng = engines.find((e) => e.id === r.engine_id);
    return { icon: Layers, name: eng?.name || r.engine_id, id: r.engine_id, sub: `Motor · ${r.model_id || "?"}` };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          Aliases públicos que tus apps usan como valor de <code>model</code>.
        </div>
        <Button
          data-testid={TID.gateway.addRouteBtn}
          onClick={() => setAdding(true)}
          disabled={disabled}
          size="sm"
          className="gap-2 bg-white text-black hover:bg-zinc-200"
        >
          <Plus className="h-3.5 w-3.5" /> Nueva ruta
        </Button>
      </div>

      <div className="rounded-md border border-zinc-800 bg-zinc-950 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-[11px] uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Alias</th>
              <th className="text-left px-4 py-2 font-medium">Tipo</th>
              <th className="text-left px-4 py-2 font-medium">Destino</th>
              <th className="text-right px-4 py-2 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900">
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-6 text-zinc-500 text-xs text-center">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-6 text-zinc-500 text-xs text-center">Sin rutas todavía.</td></tr>
            ) : items.map((r) => {
              const t = describeTarget(r);
              const Icon = t.icon;
              const isLegacy = r.target_type === "engine";
              return (
                <tr key={r.alias} data-testid={TID.gateway.routeRow(r.alias)}>
                  <td className="px-4 py-2.5 font-mono text-sm text-white">{r.alias}</td>
                  <td className="px-4 py-2.5">
                    <span className={
                      "inline-flex items-center gap-1.5 text-[11px] " +
                      (isLegacy ? "text-amber-400" : "text-emerald-400")
                    }>
                      <Icon className="h-3 w-3" />
                      {isLegacy ? "Motor (legacy)" : "Web Target"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <ArrowRight className="h-3 w-3 text-zinc-600" />
                      <span>{t.name}</span>
                      <span className="text-[10px] text-zinc-500 font-mono">({t.sub})</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button
                      data-testid={TID.gateway.routeDelete(r.alias)}
                      onClick={() => remove(r.alias)}
                      className="p-1.5 text-zinc-400 hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {adding && (
        <AddRouteDialog
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); refresh(); }}
        />
      )}
    </div>
  );
}
